import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import HeroSlider from '../components/HeroSlider'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import treePlantingImg from '../assets/tree-planting.jpg'
import localExpertiseImg from '../assets/local-expertise.jpg'
import safariLandcruiserImg from '../assets/safari-landcruiser.jpg'

export default function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')
  const scrollContainerRef = useRef(null)

  const handleBooking = (experience = '') => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300
      const currentScroll = scrollContainerRef.current.scrollLeft
      scrollContainerRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  const destinationTabs = [
    {
      name: 'Bwindi',
      fullName: 'Bwindi Impenetrable',
      image: '/images/bwindi-hero.jpg',
      link: '/destinations/bwindi-impenetrable',
      category: 'Primates'
    },
    {
      name: 'Queen Elizabeth',
      fullName: 'Queen Elizabeth NP',
      image: '/images/queen-elizabeth-hero.jpg',
      link: '/destinations/queen-elizabeth',
      category: 'Big Game'
    },
    {
      name: 'Murchison Falls',
      fullName: 'Murchison Falls NP',
      image: '/images/murchison-falls-hero.jpg',
      link: '/destinations/murchison-falls',
      category: 'Big Game'
    },
    {
      name: 'Kibale',
      fullName: 'Kibale National Park',
      image: '/images/kibale-hero.jpg',
      link: '/destinations/kibale-national-park',
      category: 'Primates'
    },
    {
      name: 'Kidepo Valley',
      fullName: 'Kidepo Valley NP',
      image: '/images/kidepo-hero.jpg',
      link: '/destinations/kidepo-valley',
      category: 'Big Game'
    },
    {
      name: 'Lake Mburo',
      fullName: 'Lake Mburo NP',
      image: '/images/lake-mburo-hero.jpg',
      link: '/destinations/lake-mburo',
      category: 'Big Game'
    },
    {
      name: 'Rwenzori Mountains',
      fullName: 'Rwenzori Mountains',
      image: '/images/rwenzori-hero.jpg',
      link: '/destinations/rwenzori-mountains',
      category: 'Mountains'
    },
    {
      name: 'Semuliki Valley',
      fullName: 'Semuliki Valley',
      image: '/images/semuliki-hero.jpg',
      link: '/destinations/semuliki-valley',
      category: 'Birding'
    },
    {
      name: 'Mount Elgon',
      fullName: 'Mount Elgon NP',
      image: '/images/mount-elgon-hero.jpg',
      link: '/destinations/mount-elgon',
      category: 'Mountains'
    }
  ]

  const experiencesScrollRef = useRef(null)

  const scrollExperiences = (direction) => {
    const container = experiencesScrollRef.current
    if (container) {
      const scrollAmount = direction === 'left' ? -400 : 400
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  const experiences = [
    {
      slug: '12-days-wildlife-safari',
      title: '12 Days Uganda Wildlife Safari Tour',
      duration: '12 Days',
      description: 'Perfect balance of primates and wildlife across five magnificent national parks',
      price: 'From $3,800pp',
      image: '/images/packages/12-days-safari.jpg',
      highlights: ['Mountain gorillas in Bwindi', 'Chimpanzees in Kibale', 'Big Five wildlife', 'Kazinga Channel cruise'],
      route: '/packages/12-days-wildlife-safari'
    },
    {
      slug: '8-days-highlights-safari',
      title: '8 Days Uganda Safari Highlights',
      duration: '8 Days',
      description: 'Uganda\'s greatest hits in one perfect week-plus adventure',
      price: 'From $2,806pp',
      image: '/images/packages/8-days-highlights.jpg',
      highlights: ['Gorilla trekking', 'Chimp tracking', 'Murchison Falls', 'Tree-climbing lions'],
      route: '/packages/8-days-highlights-safari'
    },
    {
      slug: '7-days-gorilla-chimp-safari',
      title: '7 Days Gorilla & Chimpanzee Safari',
      duration: '7 Days',
      description: 'Ultimate primate experience combining gorillas and chimps',
      price: 'Contact Us',
      image: '/images/packages/7-days-gorilla-chimp.jpg',
      highlights: ['Mountain gorillas', 'Chimpanzee tracking', 'Queen Elizabeth NP', 'Primate focus'],
      route: '/packages/7-days-gorilla-chimp-safari'
    },
    {
      slug: '5-days-gorillas-lions',
      title: '5 Day Gorillas and Lions Tour',
      duration: '5 Days',
      description: 'Maximum impact safari for limited time travelers',
      price: 'From $2,100pp',
      image: '/images/packages/5-days-gorillas-lions.jpg',
      highlights: ['Mountain gorillas', 'Tree-climbing lions', 'Ishasha sector', 'Quick adventure'],
      route: '/packages/5-days-gorillas-lions'
    },
    {
      slug: 'bwindi-gorilla-trekking',
      title: '3 Days Bwindi Gorilla Trekking',
      duration: '3 Days',
      description: 'Encounter mountain gorillas in ancient rainforest',
      price: '$750pp',
      image: '/images/packages/3-days-bwindi.jpg',
      highlights: ['Gorilla permit included', 'Expert guides', 'Forest accommodation', 'Ancient rainforest'],
      route: '/experiences/bwindi-gorilla-trekking'
    },
    {
      slug: 'queen-elizabeth-safari',
      title: '3 Days Queen Elizabeth Safari',
      duration: '3 Days',
      description: 'Tree-climbing lions and Kazinga Channel boat safari',
      price: '$450pp',
      image: '/images/packages/3-days-queen-elizabeth.jpg',
      highlights: ['Tree-climbing lions', 'Kazinga Channel', 'Game drives', 'Boat safari'],
      route: '/experiences/queen-elizabeth-safari'
    },
    {
      slug: 'murchison-falls-safari',
      title: '4 Days Murchison Falls Safari',
      duration: '4 Days',
      description: 'The mighty Nile\'s explosive power and Big Five wildlife',
      price: '$650pp',
      image: '/images/packages/4-days-murchison-chimp.jpg',
      highlights: ['Murchison Falls', 'Nile boat cruise', 'Big Five', 'Largest park'],
      route: '/experiences/murchison-falls-safari'
    },
    {
      slug: 'kibale-chimp-tracking',
      title: '3 Days Kibale Chimp Tracking',
      duration: '3 Days',
      description: 'Primate capital of the world - chimpanzees and 12 other species',
      price: '$200pp',
      image: '/images/chimpanzee-kibale.jpg',
      highlights: ['Chimpanzee tracking', '13 primate species', 'Bigodi wetland', 'Bird watching'],
      route: '/experiences/kibale-chimp-tracking'
    }
  ]

  const adventureActivities = [
    {
      slug: 'white-water-rafting',
      title: 'White-Water Rafting on the Nile',
      description: 'Experience the thrill of conquering the world\'s longest river with Grade 5 rapids. Perfect for adrenaline seekers looking for an unforgettable adventure in Jinja.',
      image: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800',
      duration: '1 Day',
      difficulty: 'Challenging',
      category: 'Water Sports'
    },
    {
      slug: 'mountain-hiking',
      title: 'Mountain Hiking & Trekking',
      description: 'Scale Uganda\'s majestic peaks including Mount Elgon and the Rwenzori Mountains. Discover breathtaking views, unique flora, and challenging trails.',
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800',
      duration: '3-9 Days',
      difficulty: 'Moderate to Challenging',
      category: 'Adventure'
    },
    {
      slug: 'cultural-tours',
      title: 'Cultural & Heritage Tours',
      description: 'Immerse yourself in Uganda\'s rich cultural tapestry. Visit traditional villages, meet local artisans, and experience authentic Ugandan hospitality.',
      image: 'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800',
      duration: '1-3 Days',
      difficulty: 'Easy',
      category: 'Culture'
    },
    {
      slug: 'bird-watching',
      title: 'Bird Watching Expeditions',
      description: 'Uganda is a birder\'s paradise with over 1,000 species. Join expert guides to spot rare and endemic species in diverse habitats across the country.',
      image: 'https://images.unsplash.com/photo-1444464666168-49d633b86797?w=800',
      duration: '1-7 Days',
      difficulty: 'Easy',
      category: 'Wildlife'
    },
    {
      slug: 'sport-fishing',
      title: 'Sport Fishing Adventures',
      description: 'Cast your line in Lake Victoria or the Nile for Nile perch, tilapia, and other species. Enjoy the thrill of the catch in stunning natural settings.',
      image: 'https://images.unsplash.com/photo-1534043464124-3be32fe000c9?w=800',
      duration: '1-2 Days',
      difficulty: 'Easy to Moderate',
      category: 'Water Sports'
    },
    {
      slug: 'canopy-walks',
      title: 'Forest Canopy Walks',
      description: 'Walk among the treetops in Uganda\'s pristine rainforests. Experience the forest from a unique perspective and spot primates and birds from above.',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800',
      duration: '2-4 Hours',
      difficulty: 'Easy',
      category: 'Adventure'
    },
    {
      slug: 'kayaking-canoeing',
      title: 'Kayaking & Canoeing',
      description: 'Paddle through serene lakes and rivers. Explore Lake Bunyonyi\'s islands or glide along the Nile while enjoying incredible scenery and wildlife.',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800',
      duration: '1-3 Days',
      difficulty: 'Easy to Moderate',
      category: 'Water Sports'
    }
  ]

  const accommodationStays = [
    {
      slug: 'safari-lodge',
      title: 'Luxury Safari Lodge',
      description: 'Experience ultimate comfort in the heart of the wilderness. Enjoy panoramic views, gourmet dining, and world-class service while surrounded by wildlife.',
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800',
      type: 'Safari Lodge',
      location: 'Queen Elizabeth National Park',
      price: '$250',
      rating: '4.9',
      amenities: ['Wi-Fi', 'Pool', 'Restaurant', 'Game Drives']
    },
    {
      slug: 'eco-lodge',
      title: 'Eco-Friendly Rainforest Lodge',
      description: 'Sustainable luxury nestled in pristine rainforest. Solar-powered, locally-sourced cuisine, and minimal environmental impact without compromising comfort.',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
      type: 'Eco-Lodge',
      location: 'Bwindi Impenetrable Forest',
      price: '$180',
      rating: '4.8',
      amenities: ['Eco-Friendly', 'Organic Food', 'Guided Walks', 'Spa']
    },
    {
      slug: 'community-homestay',
      title: 'Traditional Community Homestay',
      description: 'Live with local families and experience authentic Ugandan culture. Share meals, stories, and daily life while supporting community development.',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
      type: 'Homestay',
      location: 'Various Locations',
      price: '$40',
      rating: '4.7',
      amenities: ['Cultural Experience', 'Home Cooking', 'Local Guides', 'Community Tours']
    },
    {
      slug: 'luxury-tented-camp',
      title: 'Luxury Tented Camp',
      description: 'Glamping at its finest with spacious canvas suites, private decks, and stunning savanna views. Comfort meets adventure in style.',
      image: 'https://images.unsplash.com/photo-1618767689160-da3fb810aad7?w=800',
      type: 'Luxury Camp',
      location: 'Murchison Falls National Park',
      price: '$300',
      rating: '4.9',
      amenities: ['Private Deck', 'Butler Service', 'Gourmet Dining', 'Game Viewing']
    },
    {
      slug: 'budget-hostel',
      title: 'Backpacker Hostel & Camping',
      description: 'Budget-friendly accommodation perfect for adventure travelers. Clean, safe, and social with camping options and dormitory beds.',
      image: 'https://images.unsplash.com/photo-1496417263034-38ec4f0b665a?w=800',
      type: 'Budget',
      location: 'Jinja',
      price: '$15',
      rating: '4.5',
      amenities: ['Free Wi-Fi', 'Shared Kitchen', 'Camping', 'Social Areas']
    },
    {
      slug: 'lakeside-resort',
      title: 'Lakeside Boutique Resort',
      description: 'Relax by the water at this charming resort. Enjoy swimming, boat trips, and sunset views over Uganda\'s beautiful lakes.',
      image: 'https://images.unsplash.com/photo-1602002418816-5c0aeef426aa?w=800',
      type: 'Resort',
      location: 'Lake Bunyonyi',
      price: '$120',
      rating: '4.8',
      amenities: ['Beach Access', 'Water Sports', 'Restaurant', 'Bar']
    }
  ]

  return (
    <div>
      {/* Hero Section with Image Slider */}
      <div className="relative">
        <HeroSlider />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="container mx-auto px-4 text-center text-white z-20">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-4 font-heading drop-shadow-lg">
              Safari Adventures That Conserve
            </h1>
            <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 max-w-2xl mx-auto drop-shadow-lg">
              Experience Uganda's wildlife while protecting its natural wonders
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pointer-events-auto">
              <Link to="/plan-your-trip" className="btn-primary text-lg">
                Start Your Adventure
              </Link>
              <Link to="/destinations" className="btn-secondary text-lg">
                Explore Destinations
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Shakes Travel */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Why Safari with Shakes Travel?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're a Ugandan-owned safari company dedicated to showcasing Uganda's natural wonders through authentic safari adventure experiences—while actively conserving the environments and communities that make these journeys possible
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card group flex flex-col h-full">
              <div className="relative h-64 overflow-hidden flex-shrink-0">
                <img
                  src={localExpertiseImg}
                  alt="Local Expertise"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold mb-3 text-safari-green">Uganda Safari Specialists</h3>
                <p className="text-gray-600 text-sm flex-grow">
                  As a Ugandan-owned safari company, we bring unmatched local expertise and authentic connections to Uganda's national parks, wildlife, communities, and conservation partners—ensuring genuine, immersive safari experiences
                </p>
              </div>
            </div>

            <Link to="/planting-green-paths" className="card group flex flex-col h-full no-underline">
              <div className="relative h-64 overflow-hidden flex-shrink-0">
                <img
                  src={treePlantingImg}
                  alt="Planting Green Paths"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold mb-3 text-safari-green">Every Safari Plants Trees</h3>
                <p className="text-gray-600 text-sm flex-grow">
                  Through our Planting Green Paths initiative, your safari directly funds tree planting and habitat restoration across Uganda's destinations—protecting the incredible places you visit while empowering local communities
                </p>
              </div>
            </Link>

            <div className="card group flex flex-col h-full">
              <div className="relative h-64 overflow-hidden flex-shrink-0">
                <img
                  src={safariLandcruiserImg}
                  alt="Curated Adventures"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold mb-3 text-safari-green">Expertly Curated Safari Adventures</h3>
                <p className="text-gray-600 text-sm flex-grow">
                  We specialize in safari experiences—from gorilla trekking in ancient rainforests to wildlife safaris across savannah plains. Each journey thoughtfully showcases Uganda's incredible biodiversity while supporting conservation efforts
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Destinations - Tab Slider */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-safari-green">Explore Destinations</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover Uganda's stunning national parks and natural wonders
            </p>
          </div>

          {/* Destination Tabs Slider */}
          <div className="relative max-w-6xl mx-auto">
            {/* Left Arrow */}
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden md:block"
              aria-label="Scroll left"
            >
              <svg className="w-6 h-6 text-safari-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Scrollable Container */}
            <div
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth px-2 md:px-12"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {destinationTabs.map((destination, index) => (
                <Link
                  key={index}
                  to={destination.link}
                  className="flex-shrink-0 group relative"
                  style={{ width: '200px' }}
                >
                  <div className="relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    {/* Image */}
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={destination.image}
                        alt={destination.fullName}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    </div>

                    {/* Destination Name */}
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-white font-semibold text-sm leading-tight drop-shadow-lg">
                        {destination.name}
                      </h3>
                      {destination.category && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-safari-green/80 text-white text-xs rounded-full">
                          {destination.category}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden md:block"
              aria-label="Scroll right"
            >
              <svg className="w-6 h-6 text-safari-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="text-center mt-8">
            <Link to="/destinations" className="btn-primary text-lg">
              View All Destinations
            </Link>
          </div>
        </div>
      </section>

      {/* Safari Experiences */}
      <section className="py-20 bg-safari-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-safari-green">Safari Experiences</h2>
            <p className="text-xl text-gray-600">
              Immerse yourself in Uganda's most spectacular wildlife encounters and natural landscapes—from mountain gorilla trekking to Big Five safaris, boat expeditions, and cultural experiences. Each safari adventure contributes to conservation
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative max-w-7xl mx-auto">
            {/* Left Arrow */}
            <button
              onClick={() => scrollExperiences('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden md:block"
              aria-label="Scroll left"
            >
              <svg className="w-6 h-6 text-safari-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Scrollable Container */}
            <div
              ref={experiencesScrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-2 md:px-12 py-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {experiences.map((pkg, index) => (
                <Link
                  key={index}
                  to={pkg.route}
                  className="flex-shrink-0 bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  style={{ width: '320px' }}
                >
                  <div className="relative h-48">
                    <img src={pkg.image} alt={pkg.title} className="w-full h-full object-cover" />
                    <div className="absolute top-4 right-4 bg-safari-gold text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {pkg.duration}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-2 text-safari-green line-clamp-2">{pkg.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{pkg.description}</p>
                    <ul className="text-xs text-gray-500 mb-4 space-y-1">
                      {pkg.highlights.slice(0, 3).map((highlight, i) => (
                        <li key={i} className="flex items-center">
                          <FontAwesomeIcon icon="fa-check" className="w-3 h-3 text-safari-green mr-2 flex-shrink-0" />
                          <span className="line-clamp-1">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-safari-green font-bold">{pkg.price}</span>
                      <span className="text-sm text-safari-gold hover:text-safari-green transition">
                        View Details →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => scrollExperiences('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden md:block"
              aria-label="Scroll right"
            >
              <svg className="w-6 h-6 text-safari-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="text-center mt-12">
            <Link to="/experiences" className="btn-primary text-lg">
              Explore All Experiences
            </Link>
          </div>
        </div>
      </section>







      {/* Call to Action */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center" style={{ border: '1px solid #24684f' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-safari-green">
              Safari with Purpose. Experience with Impact.
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-700">
              Start your Uganda safari adventure with a company that puts conservation first. Discover extraordinary wildlife, stunning landscapes, and vibrant culture—while actively contributing to the protection of Uganda's natural heritage through every journey.
            </p>
            <button onClick={() => handleBooking()} className="btn-primary text-lg">
              Book Your Adventure Today
            </button>
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
